import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCapabilities,
  fetchSnapshot,
  flushPendingReports,
  formatCapabilityMessages,
  submitReport,
} from "./api";
import { DEFAULT_SPOT_ID, initialSnapshot } from "./data";
import { isSpotId } from "./spots";
import {
  deleteSavedRegion,
  getPendingReports,
  getSavedRegion,
  saveRegion,
} from "./storage";
import type {
  DataSnapshot,
  DataMode,
  PendingReport,
  PublicCapabilities,
  ReportPhotoDraft,
  SavedRegionPack,
  SpotId,
  StatusObservation,
} from "./types";
import { OperationsDashboard } from "./components/OperationsDashboard";
import { UserDashboard } from "./components/UserDashboard";

const routeFromLocation = () =>
  window.location.hash === "#operations" ? "operations" : "user";

const initialSpotFromLocation = (): SpotId => {
  const requested = new URLSearchParams(window.location.search).get("spot");
  return requested && isSpotId(requested) ? requested : DEFAULT_SPOT_ID;
};

const unavailableCapabilities: PublicCapabilities = {
  schemaVersion: 1,
  communityReporting: false,
  photoUpload: false,
  routing: false,
  reasons: {
    communityReporting: "capabilities_unavailable",
    photoUpload: "capabilities_unavailable",
    routing: "capabilities_unavailable",
  },
};

type SnapshotResult = Awaited<ReturnType<typeof fetchSnapshot>>;
type SnapshotSyncState = {
  status: "idle" | "loading" | "success" | "error";
  lastSuccessAt?: string;
  errorMessage?: string;
};
type RequestFlight<T> = {
  controller: AbortController;
  promise: Promise<T>;
};
type SnapshotFlight = RequestFlight<SnapshotResult> & { spotId: SpotId };

const SNAPSHOT_REFRESH_ERROR =
  "最新情報を取得できませんでした。最後に取得できた情報を表示しています。";
const SAVED_DATA_READ_ERROR =
  "端末の保存データを読み込めませんでした。通信できる場合は最新情報の取得を続けます。";

const isAuthoritativeMode = (mode: DataMode) =>
  mode === "api" || mode === "saved";

export function App() {
  const initialSpotId = useRef(initialSpotFromLocation()).current;
  const [route, setRoute] = useState<"user" | "operations">(routeFromLocation);
  const [snapshot, setSnapshot] = useState<DataSnapshot>(initialSnapshot);
  const [dataMode, setDataMode] = useState<DataMode>("demo");
  const [observations, setObservations] = useState<StatusObservation[]>(
    initialSnapshot.observations,
  );
  const [saved, setSaved] = useState<SavedRegionPack>();
  const [pendingReports, setPendingReports] = useState(0);
  const [selectedSpotId, setSelectedSpotId] = useState<SpotId>(initialSpotId);
  const [spotLoading, setSpotLoading] = useState(false);
  const [capabilities, setCapabilities] = useState<PublicCapabilities>(
    unavailableCapabilities,
  );
  const [lastSyncedAt, setLastSyncedAt] = useState<string>();
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncState, setSyncState] = useState<SnapshotSyncState>({
    status: "idle",
  });
  const [appNotice, setAppNotice] = useState<string>();

  const mountedRef = useRef(true);
  const snapshotRequestSequence = useRef(0);
  const capabilityRequestSequence = useRef(0);
  const desiredSpotIdRef = useRef<SpotId>(initialSpotId);
  const currentSpotIdRef = useRef<SpotId>(DEFAULT_SPOT_ID);
  const currentDataModeRef = useRef<DataMode>("demo");
  const hasSuccessfulSnapshotRef = useRef(false);
  const spotChangeInFlightRef = useRef(false);
  const snapshotFlightRef = useRef<SnapshotFlight | undefined>(undefined);
  const capabilityFlightRef = useRef<
    RequestFlight<PublicCapabilities> | undefined
  >(undefined);

  const applySnapshot = useCallback(
    (
      nextSnapshot: DataSnapshot,
      mode: DataMode,
      successfulAt: string,
    ) => {
      currentSpotIdRef.current = nextSnapshot.region.spotId;
      currentDataModeRef.current = mode;
      hasSuccessfulSnapshotRef.current = true;
      setSnapshot(nextSnapshot);
      setObservations(nextSnapshot.observations);
      setDataMode(mode);
      setSelectedSpotId(nextSnapshot.region.spotId);
      setLastSyncedAt(successfulAt);
      setSyncState({ status: "success", lastSuccessAt: successfulAt });
    },
    [],
  );

  const loadSavedPack = useCallback(async (spotId?: SpotId) => {
    try {
      return await getSavedRegion(spotId);
    } catch {
      if (mountedRef.current) setAppNotice(SAVED_DATA_READ_ERROR);
      return undefined;
    }
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      const reports = await getPendingReports();
      if (mountedRef.current) setPendingReports(reports.length);
      return reports.length;
    } catch {
      if (mountedRef.current) {
        setAppNotice(
          "端末内の保留中レポートを確認できませんでした。最新情報の閲覧は続けられます。",
        );
      }
      return undefined;
    }
  }, []);

  const startSnapshotFlight = useCallback((spotId: SpotId) => {
    const current = snapshotFlightRef.current;
    if (current?.spotId === spotId && !current.controller.signal.aborted) {
      return current.promise;
    }
    current?.controller.abort("spot_changed");

    const controller = new AbortController();
    const promise = fetchSnapshot(spotId, {
      signal: controller.signal,
      fallbackToDemo: false,
    });
    const flight: SnapshotFlight = { spotId, controller, promise };
    snapshotFlightRef.current = flight;
    void promise.then(
      () => {
        if (snapshotFlightRef.current === flight) snapshotFlightRef.current = undefined;
      },
      () => {
        if (snapshotFlightRef.current === flight) snapshotFlightRef.current = undefined;
      },
    );
    return promise;
  }, []);

  const refreshSnapshot = useCallback(
    async (spotId: SpotId = DEFAULT_SPOT_ID) => {
      const requestSequence = ++snapshotRequestSequence.current;
      if (mountedRef.current) {
        setRefreshing(true);
        setSyncState((current) => ({
          status: "loading",
          lastSuccessAt: current.lastSuccessAt,
        }));
      }

      try {
        const result = await startSnapshotFlight(spotId);
        if (
          !mountedRef.current ||
          requestSequence !== snapshotRequestSequence.current ||
          desiredSpotIdRef.current !== spotId
        ) {
          return undefined;
        }
        if (result.snapshot.region.spotId !== spotId) {
          throw new Error("snapshot_spot_mismatch");
        }
        if (
          isAuthoritativeMode(currentDataModeRef.current) &&
          result.source !== "api"
        ) {
          throw new Error("snapshot_authoritative_source_unavailable");
        }

        const successfulAt = new Date().toISOString();
        applySnapshot(result.snapshot, result.source, successfulAt);
        return result.snapshot;
      } catch {
        if (
          !mountedRef.current ||
          requestSequence !== snapshotRequestSequence.current ||
          desiredSpotIdRef.current !== spotId
        ) {
          return undefined;
        }

        // A failed refresh of the currently displayed region must never
        // replace it. For a region switch, a separately saved pack is a safe
        // fallback and also allows offline switching between saved regions.
        if (
          !hasSuccessfulSnapshotRef.current ||
          currentSpotIdRef.current !== spotId
        ) {
          const savedPack = await loadSavedPack(spotId);
          if (
            savedPack &&
            mountedRef.current &&
            requestSequence === snapshotRequestSequence.current &&
            desiredSpotIdRef.current === spotId
          ) {
            setSaved(savedPack);
            applySnapshot(savedPack.snapshot, "saved", savedPack.savedAt);
            return savedPack.snapshot;
          }
        }

        setSyncState((current) => ({
          status: "error",
          lastSuccessAt: current.lastSuccessAt,
          errorMessage: SNAPSHOT_REFRESH_ERROR,
        }));
        return undefined;
      } finally {
        if (
          mountedRef.current &&
          requestSequence === snapshotRequestSequence.current
        ) {
          setRefreshing(false);
        }
      }
    },
    [applySnapshot, loadSavedPack, startSnapshotFlight],
  );

  const startCapabilityFlight = useCallback(() => {
    const current = capabilityFlightRef.current;
    if (current && !current.controller.signal.aborted) return current.promise;

    const controller = new AbortController();
    const promise = fetchCapabilities({ signal: controller.signal });
    const flight: RequestFlight<PublicCapabilities> = { controller, promise };
    capabilityFlightRef.current = flight;
    void promise.then(
      () => {
        if (capabilityFlightRef.current === flight) capabilityFlightRef.current = undefined;
      },
      () => {
        if (capabilityFlightRef.current === flight) capabilityFlightRef.current = undefined;
      },
    );
    return promise;
  }, []);

  const refreshCapabilities = useCallback(async () => {
    const requestSequence = ++capabilityRequestSequence.current;
    try {
      const nextCapabilities = await startCapabilityFlight();
      if (
        mountedRef.current &&
        requestSequence === capabilityRequestSequence.current
      ) {
        setCapabilities(nextCapabilities);
      }
      return nextCapabilities;
    } catch {
      if (
        mountedRef.current &&
        requestSequence === capabilityRequestSequence.current
      ) {
        setCapabilities(unavailableCapabilities);
      }
      return unavailableCapabilities;
    }
  }, [startCapabilityFlight]);

  useEffect(() => {
    const onHash = () => setRoute(routeFromLocation());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    document.title =
      route === "operations"
        ? "運営管理 | 東京トイレマップ"
        : "東京トイレマップ";
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const heading = document.querySelector<HTMLElement>(
      route === "operations"
        ? "#operations-main-content h1"
        : "#main-content h1",
    );
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }, [route]);

  useEffect(
    () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        snapshotRequestSequence.current += 1;
        capabilityRequestSequence.current += 1;
        snapshotFlightRef.current?.controller.abort("app_unmounted");
        capabilityFlightRef.current?.controller.abort("app_unmounted");
      };
    },
    [],
  );

  useEffect(() => {
    const updateOnlineState = () => {
      const nextOnline = navigator.onLine;
      setOnline(nextOnline);
      if (!nextOnline) {
        snapshotRequestSequence.current += 1;
        capabilityRequestSequence.current += 1;
        snapshotFlightRef.current?.controller.abort("offline");
        capabilityFlightRef.current?.controller.abort("offline");
        setRefreshing(false);
        setSyncState((current) =>
          current.status === "loading"
            ? {
                status: "error",
                lastSuccessAt: current.lastSuccessAt,
                errorMessage:
                  "オフラインになりました。最後に取得できた情報を表示しています。",
              }
            : current,
        );
      }
    };
    window.addEventListener("online", updateOnlineState);
    window.addEventListener("offline", updateOnlineState);
    return () => {
      window.removeEventListener("online", updateOnlineState);
      window.removeEventListener("offline", updateOnlineState);
    };
  }, []);

  useEffect(() => {
    void refreshCapabilities();
  }, [refreshCapabilities]);

  useEffect(() => {
    let cancelled = false;
    void refreshPendingCount();

    // IndexedDB and the network are independent. Start both immediately so a
    // failed or slow local read never blocks fresh public data.
    const savedPackPromise = loadSavedPack();
    const snapshotPromise = navigator.onLine
      ? refreshSnapshot(desiredSpotIdRef.current)
      : Promise.resolve(undefined);
    void Promise.all([savedPackPromise, snapshotPromise]).then(
      ([savedPack, refreshedSnapshot]) => {
        if (cancelled || !mountedRef.current) return;
        if (savedPack) setSaved(savedPack);
        if (!refreshedSnapshot && savedPack && !hasSuccessfulSnapshotRef.current) {
          desiredSpotIdRef.current = savedPack.snapshot.region.spotId;
          applySnapshot(savedPack.snapshot, "saved", savedPack.savedAt);
        } else if (
          !refreshedSnapshot &&
          !savedPack &&
          !navigator.onLine
        ) {
          setSyncState({
            status: "error",
            errorMessage:
              "オフラインで、端末に保存された地域データもありません。",
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [applySnapshot, loadSavedPack, refreshPendingCount, refreshSnapshot]);

  useEffect(() => {
    const flush = async () => {
      if (!navigator.onLine) return;
      try {
        const result = await flushPendingReports();
        if (!mountedRef.current) return;
        setPendingReports(result.remaining);
        if ((result.discarded ?? 0) > 0) {
          setAppNotice(
            `保留中の利用報告${result.discarded}件は、受付条件を満たさないため再送対象から除外されました。`,
          );
        }
      } catch {
        if (mountedRef.current) {
          setAppNotice(
            "保留中の利用報告を再送できませんでした。端末に保持して次回再送します。",
          );
          await refreshPendingCount();
        }
      }
    };
    void flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [refreshPendingCount]);

  useEffect(() => {
    const refreshVisibleData = () => {
      if (
        !navigator.onLine ||
        document.visibilityState === "hidden" ||
        spotChangeInFlightRef.current
      ) {
        return;
      }
      void Promise.all([
        refreshSnapshot(desiredSpotIdRef.current),
        refreshCapabilities(),
      ]);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshVisibleData();
    };
    const interval = window.setInterval(refreshVisibleData, 5 * 60_000);
    window.addEventListener("focus", refreshVisibleData);
    window.addEventListener("online", refreshVisibleData);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshVisibleData);
      window.removeEventListener("online", refreshVisibleData);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshCapabilities, refreshSnapshot]);

  const handleReport = async (
    input: Omit<PendingReport, "id" | "deviceId" | "reportedAt">,
    photo?: ReportPhotoDraft,
  ) => {
    const response = await submitReport(input, photo);
    await refreshPendingCount();
    return response;
  };

  const handleSave = async () => {
    try {
      const pack = await saveRegion({ ...snapshot, observations });
      if (mountedRef.current) setSaved(pack);
    } catch (error) {
      if (mountedRef.current) {
        setAppNotice(
          "この地域を端末に保存できませんでした。空き容量やブラウザの保存設定を確認してください。",
        );
      }
      throw error;
    }
  };

  const handleDeleteSaved = async () => {
    try {
      await deleteSavedRegion(snapshot.region.spotId);
      if (mountedRef.current) setSaved(undefined);
    } catch (error) {
      if (mountedRef.current) {
        setAppNotice("保存データを削除できませんでした。もう一度お試しください。");
      }
      throw error;
    }
  };

  const handleRefreshSaved = async () => {
    const fresh = await refreshSnapshot(snapshot.region.spotId);
    if (!fresh) {
      const error = new Error("snapshot_refresh_failed");
      if (mountedRef.current) {
        setAppNotice(
          "最新情報を取得できなかったため、端末の保存データは更新していません。",
        );
      }
      throw error;
    }
    try {
      const pack = await saveRegion(fresh);
      if (mountedRef.current) setSaved(pack);
    } catch (error) {
      if (mountedRef.current) {
        setAppNotice(
          "最新情報は取得できましたが、端末の保存データを更新できませんでした。",
        );
      }
      throw error;
    }
  };

  const handleRefresh = async () => {
    await Promise.all([
      refreshSnapshot(desiredSpotIdRef.current),
      refreshCapabilities(),
    ]);
  };

  const handleSpotChange = async (spotId: SpotId) => {
    if (
      (spotId === desiredSpotIdRef.current &&
        spotId === currentSpotIdRef.current) ||
      spotChangeInFlightRef.current
    ) {
      return;
    }
    desiredSpotIdRef.current = spotId;
    spotChangeInFlightRef.current = true;
    setSpotLoading(true);
    try {
      const nextSnapshot = await refreshSnapshot(spotId);
      if (nextSnapshot && currentSpotIdRef.current === spotId) {
        const savedPack = await loadSavedPack(spotId);
        if (
          mountedRef.current &&
          desiredSpotIdRef.current === spotId &&
          currentSpotIdRef.current === spotId
        ) {
          setSaved(savedPack);
        }
      } else if (currentSpotIdRef.current !== spotId) {
        desiredSpotIdRef.current = currentSpotIdRef.current;
      }
    } finally {
      spotChangeInFlightRef.current = false;
      if (mountedRef.current) setSpotLoading(false);
    }
  };

  const openOperations = () => {
    window.location.hash = "operations";
  };
  const closeOperations = () => {
    window.location.hash = "";
  };

  if (route === "operations") {
    return (
      <OperationsDashboard
        snapshot={snapshot}
        observations={observations}
        online={online}
        selectedSpotId={selectedSpotId}
        spotLoading={spotLoading}
        onSpotChange={handleSpotChange}
        onExit={closeOperations}
      />
    );
  }

  return (
    <UserDashboard
      snapshot={snapshot}
      dataMode={dataMode}
      capabilities={capabilities}
      capabilityMessages={formatCapabilityMessages(capabilities)}
      lastSyncedAt={lastSyncedAt}
      refreshing={refreshing}
      online={online}
      syncState={syncState}
      appNotice={appNotice}
      observations={observations}
      saved={
        saved?.snapshot.region.spotId === snapshot.region.spotId ? saved : undefined
      }
      pendingReports={pendingReports}
      selectedSpotId={selectedSpotId}
      spotLoading={spotLoading}
      onSpotChange={handleSpotChange}
      onObservation={(observation) =>
        setObservations((current) => [...current, observation])
      }
      onReport={handleReport}
      onSave={handleSave}
      onDeleteSaved={handleDeleteSaved}
      onRefreshSaved={handleRefreshSaved}
      onRefresh={handleRefresh}
      onOpenOperations={openOperations}
    />
  );
}
